import { useRef, useState } from "react";
import { Upload, Trash2, GripVertical } from "lucide-react";
import api from "../../lib/api";
import { resolveMediaUrl } from "../../lib/utils";

export default function PhotoManager({ gallery, title, hint, urls, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const dragIndex = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("gallery", gallery);
      formData.append("photo", file);
      const { data } = await api.post("/owner/business/media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data[`${gallery}_photo_urls`]);
    } catch (e) {
      setError(e.response?.data?.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (url) => {
    const { data } = await api.delete("/owner/business/media", { data: { gallery, url } });
    onChange(data[`${gallery}_photo_urls`]);
  };

  const persistOrder = async (nextUrls) => {
    onChange(nextUrls);
    await api.put("/owner/business/media/reorder", { gallery, urls: nextUrls });
  };

  const onDrop = (index) => {
    if (dragIndex.current === null || dragIndex.current === index) return;
    const next = [...urls];
    const [moved] = next.splice(dragIndex.current, 1);
    next.splice(index, 0, moved);
    dragIndex.current = null;
    persistOrder(next);
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-700">{title}</p>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted">
          <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Add photo"}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
      </div>
      {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
      {urls.length === 0 ? (
        <p className="text-xs text-muted-foreground">No photos yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {urls.map((url, index) => (
            <div
              key={url}
              draggable
              onDragStart={() => (dragIndex.current = index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(index)}
              className="group relative aspect-square overflow-hidden rounded-xl border border-border bg-muted"
            >
              <img src={resolveMediaUrl(url)} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex items-start justify-between bg-black/0 p-1 opacity-0 transition-opacity group-hover:bg-black/20 group-hover:opacity-100">
                <GripVertical className="h-4 w-4 cursor-grab text-white drop-shadow" />
                <button
                  onClick={() => remove(url)}
                  className="rounded-full bg-white/90 p-1 text-red-600 hover:bg-white"
                  title="Remove photo"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
